import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { AppError } from '../../api/contract';
import { useRepository } from '../../data/RepositoryProvider';
import { MutationError, toDialogError } from '../partners/PartnerFormDialog';
import { adminUsersRepositoryToken } from './repository';
import type { AdminUserDetail, AdminUserRole } from './types';

interface Values {
  readonly role: AdminUserRole;
  readonly partnerId: string;
  readonly agentId: string;
  readonly permissionProfileId: string;
  readonly reason: string;
}

export function AdminUserAccessDialog({
  onClose,
  onSaved,
  open,
  user,
}: {
  readonly onClose: () => void;
  readonly onSaved: (user: AdminUserDetail) => void;
  readonly open: boolean;
  readonly user: AdminUserDetail;
}) {
  const repository = useRepository(adminUsersRepositoryToken);
  const { t } = useTranslation();
  const guard = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
    watch,
  } = useForm<Values>({ defaultValues: values(user) });
  const role = watch('role');
  useEffect(() => {
    if (open) {
      reset(values(user));
      setError(null);
      guard.current = false;
      setBusy(false);
    }
  }, [open, reset, user]);
  const close = () => {
    if (!guard.current) onClose();
  };
  const submit = handleSubmit(async (form) => {
    if (guard.current) return;
    guard.current = true;
    setBusy(true);
    setError(null);
    try {
      const access =
        form.role === 'SUPER_ADMIN'
          ? { role: 'SUPER_ADMIN' as const }
          : form.role === 'AGENT_SUPER_USER'
            ? {
                role: 'AGENT_SUPER_USER' as const,
                partnerId: form.partnerId.trim(),
                agentId: form.agentId.trim(),
              }
            : {
                role: 'AGENT_ADMIN_USER' as const,
                partnerId: form.partnerId.trim(),
                agentId: form.agentId.trim(),
                permissionProfileId: form.permissionProfileId.trim(),
              };
      const result = await repository.changeAccess(user.id, { access, reason: form.reason.trim() });
      onSaved(result.data);
      onClose();
    } catch (cause) {
      setError(toDialogError(cause, t('adminUsers.errors.mutation')));
    } finally {
      guard.current = false;
      setBusy(false);
    }
  });
  const required = {
    required: t('adminUsers.validation.required'),
    validate: (value: string) => value.trim().length > 0 || t('adminUsers.validation.required'),
  };
  return (
    <Dialog fullWidth maxWidth="sm" onClose={close} open={open}>
      <DialogTitle>{t('adminUsers.access.title')}</DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          id="admin-user-access-form"
          noValidate
          onSubmit={submit}
          spacing={2}
          sx={{ pt: 1 }}
        >
          {error === null ? null : <MutationError error={error} />}
          <Alert severity="warning">
            {t('adminUsers.access.impact', { name: user.displayName, role: user.role })}
          </Alert>
          <TextField
            defaultValue={user.role}
            label={t('adminUsers.fields.role')}
            select
            {...register('role')}
          >
            <MenuItem value="SUPER_ADMIN">SUPER_ADMIN</MenuItem>
            <MenuItem value="AGENT_SUPER_USER">AGENT_SUPER_USER</MenuItem>
            <MenuItem value="AGENT_ADMIN_USER">AGENT_ADMIN_USER</MenuItem>
          </TextField>
          {role === 'SUPER_ADMIN' ? null : (
            <>
              <TextField
                error={!!errors.partnerId}
                helperText={errors.partnerId?.message}
                label={t('adminUsers.fields.partnerId')}
                required
                {...register('partnerId', required)}
              />
              <TextField
                error={!!errors.agentId}
                helperText={errors.agentId?.message}
                label={t('adminUsers.fields.agentId')}
                required
                {...register('agentId', required)}
              />
            </>
          )}
          {role !== 'AGENT_ADMIN_USER' ? null : (
            <TextField
              error={!!errors.permissionProfileId}
              helperText={errors.permissionProfileId?.message}
              label={t('adminUsers.fields.profileId')}
              required
              {...register('permissionProfileId', required)}
            />
          )}
          <TextField
            error={!!errors.reason}
            helperText={errors.reason?.message}
            label={t('adminUsers.fields.reason')}
            multiline
            required
            {...register('reason', required)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={close}>
          {t('actions.cancel')}
        </Button>
        <Button form="admin-user-access-form" loading={busy} type="submit" variant="contained">
          {t('actions.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function values(user: AdminUserDetail): Values {
  return {
    role: user.role,
    partnerId: user.partnerId ?? '',
    agentId: user.agentId ?? '',
    permissionProfileId: user.permissionProfile?.id ?? '',
    reason: '',
  };
}
